import React, { useEffect, useState } from "react";
import { api, resolveMedia } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { SectionTitle } from "@/components/SectionTitle";
import { Crosshair, LockSimple, Star, Trophy } from "@phosphor-icons/react";
import { ChampionBanner, LeaderBanner, ResultsTable, TiebreakerPanel, finalRows, leadersOf } from "@/components/ShootoutResults";
import { ShootoutShareModal, ShareCardButton } from "@/components/ShareCardModal";

const StatusChip = ({ status }) => {
  if (status === "selected") {
    return <span data-testid="shootout-chip-selected" className="flex items-center gap-1 bg-[#FF5722] text-black text-[10px] font-bold uppercase tracking-wider px-2 py-0.5"><Star size={11} weight="fill" /> Selected</span>;
  }
  if (status === "not_selected") {
    return <span className="border border-white/15 text-white/40 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">Not selected</span>;
  }
  return <span className="border border-[#FFEA00]/40 text-[#FFEA00] text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">Candidate</span>;
};

const PlayerFace = ({ c, size = "w-14 h-14" }) => (
  c.photo_url
    ? <img src={resolveMedia(c.photo_url)} alt={c.name} className={`${size} object-cover shrink-0`} />
    : <span className={`${size} flex items-center justify-center font-display text-2xl shrink-0`} style={{ backgroundColor: c.team_color || "#FF5722", color: "#000" }}>{(c.name || "?").charAt(0)}</span>
);

function CandidateCard({ c, big }) {
  const selected = c.status === "selected";
  const dim = c.status === "not_selected";
  return (
    <div
      data-testid={`shootout-candidate-${c.player_id}`}
      className={`border bg-[#121212] p-4 flex items-center gap-4 transition-colors ${selected ? "border-[#FF5722] bg-gradient-to-r from-[#FF5722]/15 to-transparent" : "border-white/10"} ${dim ? "opacity-50" : ""}`}
    >
      <PlayerFace c={c} size={big ? "w-16 h-16" : "w-14 h-14"} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className={`font-display uppercase truncate leading-tight ${big ? "text-2xl" : "text-xl"}`}>{c.name}</p>
          {c.number != null && c.number !== "" && <span className="text-white/50 font-display text-lg">#{c.number}</span>}
        </div>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {c.team_logo
            ? <img src={resolveMedia(c.team_logo)} alt="" className="w-4 h-4 object-contain" />
            : <span className="w-4 h-4 shrink-0" style={{ backgroundColor: c.team_color || "#FF5722" }} />}
          <span className="text-white/60 text-xs uppercase tracking-wide truncate">{c.team_name}</span>
          {c.rank && <span className="text-[10px] uppercase tracking-wide font-bold text-[#00E676]">Team #{c.rank}</span>}
          <StatusChip status={c.status} />
        </div>
      </div>
      <div className="text-right shrink-0">
        <p className={`font-display text-[#FF5722] tabular-nums leading-none ${big ? "text-4xl" : "text-3xl"}`} data-testid={`shootout-tpm-${c.player_id}`}>{c.tpm}</p>
        <p className="text-[10px] uppercase tracking-wider text-white/40 font-bold">3PM</p>
      </div>
    </div>
  );
}

export default function Shootout() {
  const [data, setData] = useState(null);
  const { settings } = useSettings();
  const divisions = settings.divisions || [];
  const [division, setDivision] = useState(null);
  const [share, setShare] = useState(false);

  useEffect(() => {
    const fetch = () => api.get("/shootout").then((r) => setData(r.data)).catch(() => setData((d) => d || { locked: false, candidates: [] }));
    fetch();
    const t = setInterval(fetch, 8000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (divisions.length && !divisions.includes(division)) setDivision(divisions[0]);
  }, [divisions, division]);

  if (!data) return <div className="max-w-7xl mx-auto px-6 py-16 text-white/40">Loading…</div>;

  const cands = (data.candidates || []).filter((c) => !division || c.division === division || !c.division);
  const selected = cands.filter((c) => c.status === "selected");
  const shown = data.locked ? selected : cands;
  const completed = cands.filter((c) => c.completed).length;
  const leaders = leadersOf(cands);
  const final = data.final;
  const finalists = finalRows(data.candidates || [], final);

  const byTeam = [];
  const idx = {};
  shown.forEach((c) => {
    if (idx[c.team_id] == null) {
      idx[c.team_id] = byTeam.length;
      byTeam.push({ team_id: c.team_id, team_name: c.team_name, team_logo: c.team_logo, team_color: c.team_color, division: c.division, list: [] });
    }
    byTeam[idx[c.team_id]].list.push(c);
  });

  return (
    <div className="max-w-7xl mx-auto px-6 py-16" data-testid="shootout-page">
      <SectionTitle
        overline="Long range"
        title="3-Point Shootout"
        sub={data.locked
          ? "The field is set — here are the official 3-Point Shootout participants."
          : "The top 2 three-point shooters from every team qualify automatically, straight from the Scorekeeper stats. Selected players are highlighted."}
      />

      {final?.active && (
        <div className="mb-14" data-testid="shootout-final-results">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <Trophy size={22} weight="fill" className="text-[#FFEA00]" />
            <h3 className="font-display text-2xl uppercase">Championship Round — All Divisions</h3>
            <span className="text-white/40 text-sm" data-testid="shootout-final-live-progress">Top {final.total} · {final.completed_count} of {final.total} completed</span>
            <ShareCardButton onClick={() => setShare(true)} testid="shootout-share-btn" className="ml-auto" />
          </div>
          <ChampionBanner final={final} />
          <TiebreakerPanel cands={data.candidates || []} final={final} />
          {!final.champion && !final.tiebreaker && <LeaderBanner leaders={leadersOf(finalists)} testid="shootout-final-leader" />}
          <ResultsTable list={finalists} showPrelim testid="shootout-final-table" />
        </div>
      )}

      {divisions.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-8" data-testid="shootout-division-tabs">
          {divisions.map((d) => (
            <button key={d} onClick={() => setDivision(d)}
              data-testid={`shootout-division-${d.replace(/\s+/g, "-").toLowerCase()}`}
              className={`px-5 py-2 text-sm font-bold uppercase tracking-wide transition-all duration-200 ${division === d ? "bg-[#FF5722] text-black" : "border border-white/15 text-white/70 hover:text-white"}`}>
              {d}
            </button>
          ))}
        </div>
      )}

      {data.locked && (
        <div className="flex items-center gap-2 border border-[#FF5722] bg-[#FF5722]/10 text-[#FF5722] px-4 py-3 mb-8 text-sm font-bold uppercase tracking-wide" data-testid="shootout-locked-banner">
          <LockSimple size={16} weight="fill" /> Official participants locked
        </div>
      )}

      {cands.length > 0 && (
        <div className="mb-14" data-testid="shootout-live-results">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            {!final?.active && (
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full bg-[#FF3B30] opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 bg-[#FF3B30]" />
              </span>
            )}
            <h3 className="font-display text-2xl uppercase">{final?.active ? "Preliminary Round" : "Live Results"}</h3>
            <span className="text-white/40 text-sm" data-testid="shootout-live-progress">{completed} of {cands.length} completed</span>
            <span className="text-white/30 text-[10px] uppercase tracking-widest ml-auto hidden sm:inline">Auto-updates</span>
            {!final?.active && <ShareCardButton onClick={() => setShare(true)} testid="shootout-share-btn" />}
          </div>
          {!final?.active && <LeaderBanner leaders={leaders} />}
          <ResultsTable list={cands} />
        </div>
      )}

      {!data.locked && selected.length > 0 && (
        <div className="mb-12" data-testid="shootout-selected-section">
          <div className="flex items-center gap-2 mb-4">
            <Trophy size={18} weight="fill" className="text-[#FF5722]" />
            <h3 className="font-display text-2xl uppercase">Selected so far</h3>
            <span className="text-white/40 text-sm">({selected.length})</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {selected.map((c) => <CandidateCard key={c.player_id} c={c} big />)}
          </div>
        </div>
      )}

      {shown.length === 0 ? (
        <div className="border-2 border-dashed border-white/15 py-20 text-center" data-testid="shootout-empty">
          <Crosshair size={44} className="text-white/25 mx-auto mb-4" />
          <p className="text-white/50">
            {data.locked
              ? `No selected participants in the ${division || ""} division.`
              : `No ${division || ""} division candidates yet — qualifiers appear automatically once games are scored.`}
          </p>
        </div>
      ) : data.locked ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="shootout-participants">
          {selected.map((c) => <CandidateCard key={c.player_id} c={c} big />)}
        </div>
      ) : (
        <div className="space-y-10" data-testid="shootout-teams">
          {byTeam.map((t) => (
            <section key={t.team_id || t.team_name} data-testid={`shootout-team-${t.team_id}`}>
              <div className="flex items-center gap-3 mb-4">
                {t.team_logo
                  ? <img src={resolveMedia(t.team_logo)} alt="" className="w-8 h-8 object-contain" />
                  : <span className="w-8 h-8 flex items-center justify-center font-display text-base" style={{ backgroundColor: t.team_color || "#FF5722", color: "#000" }}>{(t.team_name || "?").charAt(0)}</span>}
                <h3 className="font-display text-2xl uppercase">{t.team_name}</h3>
                {t.division && <span className="text-white/40 text-xs uppercase tracking-wide">{t.division}</span>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {t.list.map((c) => <CandidateCard key={c.player_id} c={c} />)}
              </div>
            </section>
          ))}
        </div>
      )}
      {share && <ShootoutShareModal champion={final?.champion} onClose={() => setShare(false)} />}
    </div>
  );
}
